import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message, Modal, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import CreateAdminForm, { CreateAdminFormValues } from '../components/CreateAdminForm';
import EditAdminForm, { EditAdminFormValues } from '../components/EditAdminForm';
import { AdminDTO, adminService } from '../service';

const { Title } = Typography;

const StyledContent = styled.div`
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  min-height: 280px;
`;

const Admins: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState<AdminDTO[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminDTO | null>(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAdminList();
      setAdmins(data);
    } catch (error) {
      message.error('Failed to fetch admin users');
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const getStatusColor = (status: AdminDTO['status']) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleView = (record: AdminDTO) => {
    setSelectedAdmin(record);
    setIsViewModalVisible(true);
  };

  const handleEdit = (record: AdminDTO) => {
    setSelectedAdmin(record);
    setIsEditModalVisible(true);
  };

  const handleDelete = async (record: AdminDTO) => {
    try {
      // TODO: Implement delete admin API call
      // await adminService.deleteAdmin(record.id);
      message.success('Admin user deleted successfully');
      fetchAdmins(); // Refresh the list
    } catch (error) {
      message.error('Failed to delete admin user');
      console.error('Error deleting admin:', error);
    }
  };

  const columns: ColumnsType<AdminDTO> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.first_name} ${record.last_name}`,
      sorter: (a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: AdminDTO['role']) => (
        <Tag color={role === 'root_admin' ? 'purple' : 'blue'}>
          {role === 'root_admin' ? 'Root Admin' : 'Tenant Admin'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: AdminDTO['status']) => (
        <Tag color={getStatusColor(status)}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: Date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Operations',
      key: 'operations',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Admin">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Admin">
            <Popconfirm
              title="Delete Admin"
              description="Are you sure you want to delete this admin user?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Add tenant_id column only if there are tenant admins
  if (admins.some(admin => 'tenant_id' in admin)) {
    columns.splice(2, 0, {
      title: 'Tenant ID',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      render: (_, record) => 'tenant_id' in record ? record.tenant_id : '-',
    });
  }

  const handleCreateClick = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleReset = () => {
    // Form reset is handled by the form component
  };

  const handleCreate = async (values: CreateAdminFormValues) => {
    try {
      setSubmitting(true);
      const newAdmin = await adminService.createAdmin(values);
      setAdmins(prev => [...prev, newAdmin]);
      message.success('Admin user created successfully');
      setIsModalVisible(false);
    } catch (error) {
      message.error('Failed to create admin user');
      console.error('Error creating admin:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (values: EditAdminFormValues) => {
    if (!selectedAdmin) return;
    
    try {
      setSubmitting(true);
      const updatedAdmin = await adminService.updateAdmin(selectedAdmin.id, values);
      setAdmins(prev => prev.map(admin => 
        admin.id === selectedAdmin.id ? updatedAdmin : admin
      ));
      message.success('Admin user updated successfully');
      setIsEditModalVisible(false);
    } catch (error) {
      message.error('Failed to update admin user');
      console.error('Error updating admin:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StyledContent>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Administrators Management</Title>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={handleCreateClick}
        >
          Create Admin
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={admins}
        rowKey="id"
        loading={loading}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} users`,
        }}
      />

      <Modal
        title="Create New Admin"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <CreateAdminForm
          onSubmit={handleCreate}
          onCancel={handleCancel}
          onReset={handleReset}
          submitting={submitting}
        />
      </Modal>

      <Modal
        title="Admin Details"
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedAdmin && (
          <EditAdminForm
            admin={selectedAdmin}
            onCancel={() => setIsViewModalVisible(false)}
            readonly={true}
          />
        )}
      </Modal>

      <Modal
        title="Edit Admin"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedAdmin && (
          <EditAdminForm
            admin={selectedAdmin}
            onSubmit={handleEditSubmit}
            onCancel={() => setIsEditModalVisible(false)}
            submitting={submitting}
          />
        )}
      </Modal>
    </StyledContent>
  );
};

export default Admins; 